import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { salesQuoteService } from '../../../services/salesQuoteService';
import { useSalesPointOfSale } from '../../../contexts/SalesPointOfSaleContext';
import ConfirmModal from '../../../components/Common/ConfirmModal';
import Toast from '../../../components/Common/Toast';
import TableWithMonthGroups from '../../../components/Common/TableWithMonthGroups';
import MutateGate from '../../../components/Common/MutateGate';
import { useCanMutate } from '../../../hooks/useCanMutate';
import { formatDateLocaleLima } from '../../../utils/dateUtils';
import { POS_CONFIRMED_STATE } from '../../../utils/ventasPosNavigation';

const VentasQuotesIndex = () => {
  const canMutate = useCanMutate();
  const { selectedPointOfSale } = useSalesPointOfSale();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [toggleTarget, setToggleTarget] = useState(null);
  const [actionLoading, setActionLoading] = useState(false);
  const [toast, setToast] = useState({ isOpen: false, message: '', type: 'info' });

  const load = async () => {
    if (!selectedPointOfSale?.id) return;
    setLoading(true);
    try {
      const params = { point_of_sale_id: selectedPointOfSale.id };
      if (statusFilter !== 'all') params.status = statusFilter;
      if (searchTerm) params.search = searchTerm;
      const res = await salesQuoteService.getQuotes(params);
      if (res.success) setItems(res.data || []);
    } catch {
      setToast({ isOpen: true, message: 'Error al cargar cotizaciones', type: 'error' });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, [statusFilter, selectedPointOfSale?.id]);

  const handleSearch = (e) => {
    e.preventDefault();
    load();
  };

  const getActionLabel = (quote) => {
    if (quote.can_delete) return 'Eliminar';
    if (quote.status === 'issued') return 'Cancelar';
    return 'Reactivar';
  };

  const getModalTitle = (quote) => {
    if (!quote) return '';
    if (quote.can_delete) return 'Eliminar cotización';
    if (quote.status === 'issued') return 'Cancelar cotización';
    return 'Reactivar cotización';
  };

  const getModalMessage = (quote) => {
    if (!quote) return '';
    if (quote.can_delete) {
      return `¿Eliminar la cotización "${quote.code}"? Esta acción no se puede deshacer.`;
    }
    if (quote.status === 'issued') {
      return `¿Cancelar la cotización "${quote.code}"? Tiene ventas vinculadas y solo quedará anulada.`;
    }
    return `¿Reactivar la cotización "${quote.code}"?`;
  };

  const closeToast = useCallback(() => {
    setToast((prev) => ({ ...prev, isOpen: false }));
  }, []);

  const openActionModal = (quote) => {
    closeToast();
    setActionLoading(false);
    setToggleTarget(quote);
  };

  const closeActionModal = () => {
    setActionLoading(false);
    setToggleTarget(null);
  };

  const getConfirmText = (quote) => {
    if (!quote) return 'Sí';
    if (quote.can_delete) return 'Eliminar';
    if (quote.status === 'issued') return 'Sí, cancelar';
    return 'Sí, reactivar';
  };

  const confirmToggle = async () => {
    if (!toggleTarget) return;
    setActionLoading(true);
    try {
      const res = await salesQuoteService.toggleStatus(toggleTarget.id);
      const message =
        res.deleted === true
          ? 'Cotización eliminada'
          : res.message || 'Operación completada';
      setToast({ isOpen: true, message, type: 'success' });
      await load();
    } catch (err) {
      setToast({
        isOpen: true,
        message: err.response?.data?.message || 'Error al procesar la acción',
        type: 'error',
      });
      throw err;
    } finally {
      setActionLoading(false);
    }
  };

  const formatCurrency = (amount) =>
    new Intl.NumberFormat('es-PE', { style: 'currency', currency: 'PEN' }).format(amount || 0);

  return (
    <div className="p-6">
      <div className="max-w-7xl mx-auto">
        <div className="flex justify-between items-center mb-4">
          <div>
            <h1 className="text-3xl font-bold text-gray-800">Cotización</h1>
            <p className="text-gray-600 mt-1">Cotizaciones de venta por punto de venta</p>
          </div>
          <MutateGate>
            <Link
              to="/ventas/quotes/create"
              state={POS_CONFIRMED_STATE}
              className="inline-flex items-center px-4 py-2 bg-teal-600 text-white rounded-lg hover:bg-teal-700"
            >
              + Nueva cotización
            </Link>
          </MutateGate>
        </div>

        <form onSubmit={handleSearch} className="mt-6 flex flex-wrap gap-3 items-end">
          <div>
            <label className="block text-xs text-gray-600 mb-1">Estado</label>
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="border border-gray-300 rounded-lg px-3 py-2"
            >
              <option value="all">Todos</option>
              <option value="issued">Emitidas</option>
              <option value="cancelled">Canceladas</option>
            </select>
          </div>
          <div className="flex-1 min-w-[200px]">
            <label className="block text-xs text-gray-600 mb-1">Buscar</label>
            <input
              type="text"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder="Código, cliente u observación..."
              className="w-full border border-gray-300 rounded-lg px-3 py-2"
            />
          </div>
          <button type="submit" className="px-4 py-2 bg-gray-800 text-white rounded-lg">
            Buscar
          </button>
        </form>

        <div className="mt-6 bg-white rounded-xl border border-gray-200 overflow-hidden">
          {loading ? (
            <p className="p-8 text-center text-gray-500">Cargando...</p>
          ) : items.length === 0 ? (
            <p className="p-8 text-center text-gray-500">No hay cotizaciones</p>
          ) : (
            <TableWithMonthGroups
              data={items}
              getDateForGrouping={(quote) => quote.issue_date}
              defaultCollapsed
              columnCount={10}
              emptyMessage="No hay cotizaciones"
              headerRow={
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Código</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Fecha</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Cliente</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Válida hasta</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Ítems</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Base imponible</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">IGV</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Total</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Estado</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Acciones</th>
                </tr>
              }
              renderRow={(quote) => (
                <tr key={quote.id} className="hover:bg-gray-50">
                  <td className="px-6 py-4 font-mono text-teal-700">
                    <Link to={`/ventas/quotes/${quote.id}/edit`} state={POS_CONFIRMED_STATE}>
                      {quote.code}
                    </Link>
                  </td>
                  <td className="px-6 py-4 text-sm text-gray-600">
                    {formatDateLocaleLima(quote.issue_date)}
                  </td>
                  <td className="px-6 py-4 text-sm text-gray-600">{quote.client?.name || '—'}</td>
                  <td className="px-6 py-4 text-sm text-gray-600">
                    {quote.valid_until ? formatDateLocaleLima(quote.valid_until) : '—'}
                  </td>
                  <td className="px-6 py-4 text-sm">{quote.items?.length || 0}</td>
                  <td className="px-6 py-4 text-sm">{formatCurrency(quote.base_imponible)}</td>
                  <td className="px-6 py-4 text-sm">{formatCurrency(quote.igv_amount)}</td>
                  <td className="px-6 py-4 text-sm font-medium">{formatCurrency(quote.total_amount)}</td>
                  <td className="px-6 py-4">
                    <span
                      className={`px-2 py-1 text-xs rounded-full ${
                        quote.status === 'cancelled'
                          ? 'bg-red-100 text-red-800'
                          : 'bg-green-100 text-green-800'
                      }`}
                    >
                      {quote.status === 'cancelled' ? 'Cancelada' : 'Emitida'}
                    </span>
                  </td>
                  <td className="px-6 py-4 text-sm">
                    <div className="flex items-center gap-3">
                      <Link
                        to={`/ventas/quotes/${quote.id}/edit`}
                        state={POS_CONFIRMED_STATE}
                        className="text-purple-600 hover:text-purple-900"
                      >
                        {canMutate && quote.status !== 'cancelled' ? 'Editar' : 'Ver'}
                      </Link>
                      <MutateGate>
                        <button
                          type="button"
                          onClick={() => openActionModal(quote)}
                          className={
                            quote.can_delete
                              ? 'text-red-600 hover:text-red-800'
                              : quote.status === 'issued'
                                ? 'text-orange-600 hover:text-orange-800'
                                : 'text-green-600 hover:text-green-800'
                          }
                        >
                          {getActionLabel(quote)}
                        </button>
                      </MutateGate>
                    </div>
                  </td>
                </tr>
              )}
            />
          )}
        </div>
      </div>

      <ConfirmModal
        isOpen={!!toggleTarget}
        onClose={closeActionModal}
        onConfirm={confirmToggle}
        title={getModalTitle(toggleTarget)}
        message={getModalMessage(toggleTarget)}
        confirmText={getConfirmText(toggleTarget)}
        cancelText="Volver"
        type={toggleTarget?.can_delete ? 'danger' : 'default'}
        isLoading={actionLoading}
      />
      <Toast
        isOpen={toast.isOpen}
        message={toast.message}
        type={toast.type}
        onClose={closeToast}
      />
    </div>
  );
};

export default VentasQuotesIndex;
